"use client"

import { ReactNode, useId, useRef, useState } from "react"

// Tabs segmentadas da vitrine (Story 2.7 / DESIGN.md plan-tabs). Troca de painel SEM reload
// (estado client); a ativa preenche {colors.accent-strong}. A11y: role=tablist/tab/tabpanel,
// aria-selected/aria-controls, roving tabindex + setas ←/→ (padrão WAI-ARIA de tablist).
// Alvo de toque: `min-h-11` (44px) porque `py-2` sozinho dava 36px — abaixo do mínimo
// WAI-ARIA/HIG no telemóvel. Volta ao natural em `small:` para não mexer no desktop.
// Recebe painéis JÁ renderizados no server (grids de PlanCard) como ReactNode — o card
// continua server component; este client só alterna a visibilidade.

export type PlanTab = {
  key: string
  label: string
  panel: ReactNode
}

export default function PlanTabs({ tabs }: { tabs: PlanTab[] }) {
  const [active, setActive] = useState(0)
  const baseId = useId()
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const focusTab = (index: number) => {
    setActive(index)
    tabRefs.current[index]?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault()
      const direction = event.key === "ArrowRight" ? 1 : -1
      focusTab((index + direction + tabs.length) % tabs.length)
    } else if (event.key === "Home") {
      event.preventDefault()
      focusTab(0)
    } else if (event.key === "End") {
      event.preventDefault()
      focusTab(tabs.length - 1)
    }
  }

  return (
    <div className="flex flex-col gap-10">
      <div
        role="tablist"
        aria-label="Categorias de plano"
        className="inline-flex flex-wrap items-center gap-2 self-start rounded-full border border-border bg-surface/40 p-1"
      >
        {tabs.map((tab, index) => {
          const selected = active === index
          return (
            <button
              key={tab.key}
              ref={(node) => {
                tabRefs.current[index] = node
              }}
              role="tab"
              id={`${baseId}-tab-${index}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${index}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(index)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={`inline-flex min-h-11 items-center rounded-full px-5 py-2 text-sm font-semibold transition-colors small:min-h-0 ${
                selected
                  ? "bg-accent-strong text-white"
                  : "text-subtle hover:text-text"
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {tabs.map((tab, index) => (
        <div
          key={tab.key}
          role="tabpanel"
          id={`${baseId}-panel-${index}`}
          aria-labelledby={`${baseId}-tab-${index}`}
          hidden={active !== index}
        >
          {tab.panel}
        </div>
      ))}
    </div>
  )
}
